// 工事新規作成画面
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  useImperativeHandle
} from 'react';
import '../App.css';
import {
  Grid,
  Theme,
  Button,
  Typography,
  DialogContent,
  DialogContentText,
  Dialog,
  DialogActions,
  DialogTitle
} from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { useRecoilState } from 'recoil';
import { btnColor } from '../GlobalState';
import { useDropzone } from 'react-dropzone';
import {
  deleteConstructionSiteImgById,
  getConstructionSiteImgById,
  postConstructionSiteImgById
} from '../apicalls/construction';
import {
  acceptablePictureContentTypes,
  acceptablePictureTypeStr,
  loadFile
} from '../utils/common';

type Tprops = {
  isPosted: boolean;
  isUpdated: boolean;
  construct_id: number | null;
  setOpen: any;
  setErrorMessage: any;
  resetIsUpdated: any;
};

export const ConstructionSiteImg: React.FC<Tprops> = (props: Tprops) => {
  const {
    isPosted,
    isUpdated,
    construct_id,
    setOpen,
    setErrorMessage,
    resetIsUpdated
  } = props;

  const [buttonColor] = useRecoilState(btnColor);
  const [openImageModal, setOpenImageModal] = useState<boolean>(false);
  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      subContainer: {
        marginLeft: 15,
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'row'
      },
      headerDropArea: {
        border: '1px dashed silver',
        padding: 20,
        borderRadius: 10
      },
      refButton: {
        color: '#fff'
      },
      img: {
        objectFit: 'contain',
        height: '150px',
        width: '300px'
      },
      deleteButton: {
        color: '#fff'
      }
    })
  );

  const classes = useStyles();
  // ファイル参照ボタンが押下可能か
  const [disable, setDisable] = useState<boolean>(false);
  const inputRef = useRef<any>(null);
  const [siteImage, setSiteImage] = useState<any>(null);
  const [formData, setFormData] = useState<FormData>();
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const fileTypeAccepts = acceptablePictureContentTypes();

  const fileUpload = () => {
    if (disable === true) {
      inputRef.current.click();
    }
    setDisable(false);
  };

  /**
   * ドラッグ&ドロップされたときの処理
   * @param acceptedFiles
   */
  const onDrop = (acceptedFiles: File[]) => {
    readFile(acceptedFiles[0]);
  };

  const handleImageClick = () => {
    setOpenImageModal(true);
  };

  const handleCloseImageModal = () => {
    setOpenImageModal(false);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  /**
   * ファイル選択ボタン押下時の処理
   * @param event
   */
  const onFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files !== null) {
      // 選択ファイル一覧を取得
      const file: File | null = event.target.files[0];
      readFile(file);
    }
    event.target.value = '';
  };

  /**
   * fileを読み込む処理
   * @param file
   */
  const readFile = (file: File) => {
    let format = file.type;
    const data = new FormData();
    data.append('file_obj', file as any);
    setFormData(data);
    if (file && fileTypeAccepts.includes(format)) {
      loadFile(file, setSiteImage);
    } else {
      setErrorMessage('画像ファイル以外はアップロードできません。');
      setOpen(true);
    }
  };

  const getConstructionSiteImg = async () => {
    if (construct_id !== null) {
      const response = await getConstructionSiteImgById(construct_id);
      if (response.data.data !== '') {
        if (response.data.data === '読み込み時にエラーが発生しました。') {
          setErrorMessage('現場の写真の取得に失敗しました。');
          setOpen(true);
        } else {
          setSiteImage(
            `data:${response.data.file_format};base64,${response.data.data}`
          );
        }
      }
    }
  };

  const postConstructionSiteImg = async () => {
    if (construct_id !== null && formData) {
      try {
        await postConstructionSiteImgById(construct_id, formData);
        resetIsUpdated();
      } catch (err) {
        console.log(err);
      }
    }
  };

  const deleteConstructionSiteImg = async () => {
    //まだconstructが保存されていない状態
    if (construct_id === null) {
      setFormData(undefined);
      setSiteImage(null);
    } else {
      try {
        await deleteConstructionSiteImgById(construct_id);
        setFormData(undefined);
        setSiteImage(null);
      } catch (err) {
        console.log(err);
      }
    }
  };

  useEffect(() => {
    getConstructionSiteImg();
  }, []);

  useEffect(() => {
    if (isPosted || isUpdated) {
      postConstructionSiteImg();
    }
  }, [isPosted, isUpdated]);

  const handleCloseAlert = () => {
    setOpenAlert(false);
  };

  return (
    <div className={classes.subContainer} style={{ marginBottom: 15 }}>
      {/* 画像拡大表示用モーダル */}
      <Dialog
        open={openImageModal}
        onClose={handleCloseImageModal}
        maxWidth='md'
        fullWidth
      >
        <DialogContent>
          {siteImage && (
            <img
              src={siteImage}
              alt='拡大画像'
              style={{ width: '100%', height: 'auto' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImageModal} color='primary'>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAlert}
        onClose={handleCloseAlert}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>
          {'本当に削除してよろしいですか。'}
        </DialogTitle>
        <DialogActions>
          <Button onClick={handleCloseAlert} color='primary'>
            キャンセル
          </Button>
          <Button
            onClick={() => {
              deleteConstructionSiteImg();
              setOpenAlert(false);
            }}
            color='primary'
            autoFocus
          >
            削除
          </Button>
        </DialogActions>
      </Dialog>
      <Grid container justify={'center'}>
        {/* ドラッグ＆ドロップ領域 */}
        <Grid
          item
          xs={12}
          className={classes.headerDropArea}
          {...getRootProps()}
        >
          <Typography variant='body1' align='center' gutterBottom={true}>
            {isDragActive
              ? 'ここにドロップしてください。'
              : '現場の写真をドラッグ＆ドロップしてください。'}
          </Typography>
          <Grid item style={{ textAlign: 'center' }}>
            {siteImage === null ? (
              <></>
            ) : (
              <img
                className={classes.img}
                src={siteImage}
                alt='fileImg'
                onClick={event => {
                  event.stopPropagation();
                  handleImageClick();
                }}
                style={{ cursor: 'pointer' }}
              />
            )}
          </Grid>
          <Grid
            item
            style={{ textAlign: 'center', marginTop: 10, marginBottom: 10 }}
          >
            <Button
              className={classes.refButton}
              variant='contained'
              style={{ backgroundColor: buttonColor }}
              disabled={disable}
              onClick={() => {
                setDisable(true);
                fileUpload();
              }}
            >
              ファイルを参照
            </Button>
            <input
              hidden
              ref={inputRef}
              type='file'
              id='image_name_logo'
              onChange={onFileInputChange}
            />
          </Grid>
          <>
            <Typography variant='body2' align='center' gutterBottom={true}>
              ※保存可能なファイル形式：{acceptablePictureTypeStr()}
            </Typography>
          </>
        </Grid>
        {siteImage && (
          <Grid
            item
            style={{ textAlign: 'center', marginTop: 10, marginBottom: 10 }}
          >
            <Button
              className={classes.refButton}
              variant='contained'
              style={{ backgroundColor: buttonColor }}
              disabled={disable}
              onClick={() => {
                setOpenAlert(true);
              }}
            >
              画像を削除
            </Button>
          </Grid>
        )}
      </Grid>
    </div>
  );
};
